import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { errorTextCls, helpTextCls, inputCls } from "components/tw/classes";
import {
  getStringValue,
  isCustomFieldShown,
  sanitizeNumberInput,
  type ValidationError,
} from "components/customer/build-pdf/build-pdf-utils";
import { fetch as fetchUsers } from "services/user-select.service";
import type { CustomField } from "types/CustomField";
import { isFillable } from "./leave-template-fields";

/** The label above a field of the sheet. */
export const FIELD_LABEL_CLS = "mb-1 block text-sm font-medium text-gray-700";

interface CustomFieldInputsProps {
  companyId: string;
  fields: CustomField[];
  /** By field label; empty until the person tried to send the request. */
  errors: Record<string, ValidationError>;
  onChange: (label: string, value: string) => void;
}

/**
 * The inputs of the template's fields, in the template's order. The leave calendar draws the same fields
 * with Polaris, whose pop-ups (the user list, the date picker) would sit outside this sheet and could not
 * be reached while it is open, so the sheet has plain inputs and a plain list of colleagues.
 */
export default function CustomFieldInputs({ companyId, fields, errors, onChange }: CustomFieldInputsProps) {
  const { t } = useTranslation();
  const shownFields = fields.filter((field) => isFillable(field) && isCustomFieldShown(field, fields));

  return (
    <div className="space-y-4">
      {shownFields.map((field) => {
        const error = errors[field.label];
        const inputId = `leave-sheet-field-${field.label}`;
        return (
          <div key={field.label}>
            <label htmlFor={inputId} className={FIELD_LABEL_CLS}>
              {field.name}
              {field.required && " *"}
            </label>
            <FieldControl
              id={inputId}
              companyId={companyId}
              field={field}
              hasError={Boolean(error)}
              onChange={(value) => onChange(field.label, value)}
            />
            {field.helperText && <p className={helpTextCls}>{field.helperText}</p>}
            {error && (
              <p className={errorTextCls}>{typeof error === "string" ? t(error) : t(error.key, error.params)}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface FieldControlProps {
  id: string;
  companyId: string;
  field: CustomField;
  hasError: boolean;
  onChange: (value: string) => void;
}

function FieldControl({ id, companyId, field, hasError, onChange }: FieldControlProps) {
  const { t } = useTranslation();
  const value = getStringValue(field);
  const shared = { id, className: inputCls, "aria-invalid": hasError, value };

  switch (field.type) {
    case "textarea":
      return <textarea {...shared} rows={3} onChange={(event) => onChange(event.target.value)} />;
    case "number":
      return (
        <input
          {...shared}
          type="text"
          inputMode="decimal"
          onChange={(event) => onChange(sanitizeNumberInput(event.target.value))}
        />
      );
    case "date":
      return <input {...shared} type="date" onChange={(event) => onChange(event.target.value)} />;
    case "select":
      return (
        <select {...shared} onChange={(event) => onChange(event.target.value)}>
          <option value="">{t("files_template_custom_type_select_select_an_option")}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    case "user":
    case "signature":
      return <ColleagueSelect {...shared} companyId={companyId} onChange={onChange} />;
    default:
      return <input {...shared} type="text" onChange={(event) => onChange(event.target.value)} />;
  }
}

interface Colleague {
  id: string;
  name?: string;
  email?: string;
}

/** The people of the company; a «signature» field takes one of them as the person who also signs. */
function ColleagueSelect({
  companyId,
  onChange,
  ...selectProps
}: { companyId: string; onChange: (value: string) => void } & Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "onChange"
>) {
  const { t } = useTranslation();
  const [colleagues, setColleagues] = useState<Colleague[]>([]);

  useEffect(() => {
    let cancelled = false;
    // Asked the way the document form's user list asks. Without an answer the list stays empty.
    fetchUsers(companyId, {}, false)
      .then((answer: { usersCollection?: Colleague[] }) => !cancelled && setColleagues(answer.usersCollection ?? []))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return (
    <select {...selectProps} onChange={(event) => onChange(event.target.value)}>
      <option value="">{t("select_user")}</option>
      {colleagues.map((colleague) => (
        <option key={colleague.id} value={colleague.id}>
          {colleague.name || colleague.email}
        </option>
      ))}
    </select>
  );
}
